import type { Beat, Idea } from '../api/types'
import type { Format } from './formats'

/**
 * A video's script as beats: what Martin says, and what is on screen.
 *
 * An idea starts with no script, and the editor shows the beats its format
 * calls for until he writes one. The structure is the one in AGENTS.md (hook,
 * context, action, result, insight); a question or a term is the card read
 * out loud and then answered, so those two start from the card.
 */
const TEMPLATES = {
  question: ['Question', 'Answer', 'Example', 'Takeaway'],
  term: ['Term', 'What it is', 'Example', 'Why it matters'],
  story: ['Hook', 'Context', 'Action', 'Result', 'Insight'],
} as const

/** What each beat is for, shown in the empty field. */
export const PROMPTS: Record<string, string> = {
  Hook: 'The first two seconds. What makes a stranger stop scrolling?',
  Context: 'Why should they care?',
  Action: 'The part that is actually interesting',
  Result: 'What happened',
  Insight: 'What it means for them on Monday',
  Answer: "Answer it in one breath, the way you'd say it",
  Example: 'One specific example. Numbers beat adjectives',
  Takeaway: 'The line they should remember',
  'What it is': 'One sentence a smart non-developer gets',
  'Why it matters': 'What changes for someone who gets this',
}

/** The label a beat added by hand starts with. */
export const EXTRA_BEAT = 'Beat'

export function templateFor(format: Format): Beat[] {
  const kinds = format === 'question' || format === 'term' ? TEMPLATES[format] : TEMPLATES.story
  return kinds.map((kind) => ({ kind, say: '', screen: '' }))
}

/** The beats to show: the idea's own, or its format's template while it has none. */
export function beatsOf(idea: Pick<Idea, 'script' | 'format'>): Beat[] {
  return idea.script.length ? idea.script : templateFor(idea.format)
}

/**
 * The first beat of a question or a term is the card: the title on screen,
 * read out as it stands. It has nothing to write in, and its words are the
 * title's.
 */
export function isCardBeat(beat: Beat, index: number): boolean {
  return index === 0 && (beat.kind === 'Question' || beat.kind === 'Term')
}

export function isBlank(beats: readonly Beat[]): boolean {
  return beats.every((beat) => !beat.say.trim() && !beat.screen.trim())
}

/**
 * A brisk talking pace, about 156 words a minute: what a short is spoken at.
 * Only for an estimate of the running time, never for a cut.
 */
export const WORDS_PER_SECOND = 2.6

function wordsIn(text: string): number {
  return text.split(/\s+/).filter(Boolean).length
}

/** When each beat starts, and how long the whole thing runs, in seconds. */
export function timing(beats: readonly Beat[], title: string): { starts: number[]; total: number } {
  const starts: number[] = []
  let total = 0
  beats.forEach((beat, index) => {
    starts.push(total)
    total += wordsIn(isCardBeat(beat, index) ? title : beat.say) / WORDS_PER_SECOND
  })
  return { starts, total }
}

/** `0:07`, `1:12`. */
export function clock(seconds: number): string {
  const whole = Math.round(seconds)
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

/** A Short that runs past a minute has lost most of its viewers. */
export const SHORT_LIMIT_SECONDS = 60
